import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Trash2, ArrowLeft, Save, Check } from "lucide-react";
import MOShell, { useMOAccess, isWritable, LoadingBlock, NoAccessScreen } from "@/components/masterOrder/MOShell";
import DateField from "@/components/masterOrder/DateField";
import CurrencyField from "@/components/masterOrder/CurrencyField";
import CakePlanningHelper, { isCakeLikeProductType } from "@/components/masterOrder/CakePlanningHelper";
import ProductTypeField from "@/components/masterOrder/ProductTypeField";
import StudioSelect from "@/components/masterOrder/StudioSelect";
import { Field, Input } from "@/pages/masterOrder/MOClients";
import {
  listClients, createClient, listProductTypes, createProductType,
  createOrder, createOrderItem, getSettings, DELIVERY_TYPES,
  createOrderPayment, displayOrderNumber,
} from "@/lib/masterOrderApi";
import { formatApiError } from "@/lib/api";

/* ------------------------------ Client Picker ---------------------------- */
function ClientPicker({ clients, value, onChange, onCreate }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const selected = clients.find((c) => c.id === value);
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return clients.slice(0, 8);
    return clients.filter((c) =>
      c.name.toLowerCase().includes(s) || (c.phone || "").toLowerCase().includes(s)
    ).slice(0, 12);
  }, [q, clients]);

  return (
    <div className="relative">
      {selected && !open ? (
        <div className="flex items-center justify-between h-11 px-3 rounded-lg bg-white"
             style={{ boxShadow: "inset 0 0 0 1px #EFE4D0" }} data-testid="mo-new-order-client-selected">
          <div>
            <div className="text-[13px]" style={{ color: "#2A1D14" }}>{selected.name}</div>
            <div className="text-[11px]" style={{ color: "#8B6F4B" }}>{selected.phone}</div>
          </div>
          <button onClick={() => { onChange(null); setOpen(true); }} className="text-[12px] px-3 h-8 rounded-xl"
                  style={{ boxShadow: "inset 0 0 0 1px #D9C6A6", color: "#4A3728" }}
                  data-testid="mo-new-order-client-change">
            Сменить
          </button>
        </div>
      ) : (
        <>
          <input value={q} onFocus={() => setOpen(true)} onChange={(e) => { setQ(e.target.value); setOpen(true); }}
                 placeholder="Начните вводить имя или телефон..."
                 className="w-full h-10 px-3 rounded-lg text-[13px] outline-none"
                 style={{ background: "#FBF5EA", boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}
                 data-testid="mo-new-order-client-search" />
          {open && (
            <div className="absolute z-30 mt-1 w-full rounded-xl bg-white overflow-hidden"
                 style={{ boxShadow: "0 12px 28px -12px rgba(58,43,31,0.24), inset 0 0 0 1px #EFE4D0" }}>
              <div className="max-h-56 overflow-y-auto">
                {filtered.map((c) => (
                  <button key={c.id} type="button" onClick={() => { onChange(c.id); setOpen(false); setQ(""); }}
                          className="w-full text-left px-3 py-2 text-[13px] hover:bg-black/5"
                          data-testid={`mo-new-order-client-opt-${c.id}`}>
                    <div style={{ color: "#2A1D14" }}>{c.name}</div>
                    <div className="text-[11px]" style={{ color: "#8B6F4B" }}>{c.phone}</div>
                  </button>
                ))}
                {filtered.length === 0 && (
                  <div className="px-3 py-3 text-[12px]" style={{ color: "#8B6F4B" }}>Ничего не найдено.</div>
                )}
              </div>
              <button type="button" onClick={onCreate}
                      className="w-full flex items-center gap-2 px-3 py-2 text-[12.5px] border-t"
                      style={{ borderColor: "#EFE4D0", color: "#4A3728", background: "#FBF5EA" }}
                      data-testid="mo-new-order-client-create">
                <Plus size={13} strokeWidth={1.8} /> Быстро добавить клиента
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

/* --------------------------- Quick create client ------------------------- */
function QuickClientModal({ open, onClose, onCreated }) {
  const [form, setForm] = useState({ name: "", phone: "", messenger: "" });
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setForm({ name: "", phone: "", messenger: "" }); }, [open]);
  if (!open) return null;
  const save = async () => {
    if (!form.name.trim() || !form.phone.trim()) return toast.error("Имя и телефон обязательны");
    setSaving(true);
    try {
      const c = await createClient({
        name: form.name.trim(), phone: form.phone.trim(),
        messenger: form.messenger.trim() || null,
      });
      toast.success("Клиент добавлен");
      onCreated(c);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
         style={{ background: "rgba(35,23,16,0.42)" }} onClick={onClose}>
      <div className="w-full max-w-md rounded-2xl bg-white overflow-hidden" onClick={(e) => e.stopPropagation()}
           style={{ boxShadow: "0 30px 60px -20px rgba(35,23,16,0.4)" }}>
        <div className="px-5 py-4 border-b" style={{ borderColor: "#EFE4D0" }}>
          <div className="text-[18px]" style={{ fontFamily: "'Cormorant Garamond', serif", color: "#2A1D14" }}>Быстрое добавление клиента</div>
        </div>
        <div className="p-5 space-y-3">
          <Field label="Имя *"><Input value={form.name} onChange={(v) => setForm(f => ({ ...f, name: v }))} testid="mo-quick-client-name" /></Field>
          <Field label="Телефон *"><Input value={form.phone} onChange={(v) => setForm(f => ({ ...f, phone: v }))} testid="mo-quick-client-phone" placeholder="+..." /></Field>
          <Field label="Мессенджер"><Input value={form.messenger} onChange={(v) => setForm(f => ({ ...f, messenger: v }))} /></Field>
        </div>
        <div className="p-4 border-t flex justify-end gap-2" style={{ borderColor: "#EFE4D0", background: "#FBF5EA" }}>
          <button onClick={onClose} className="h-10 px-4 rounded-xl text-[12.5px]"
                  style={{ background: "#FFFFFF", color: "#4A3728", boxShadow: "inset 0 0 0 1px #D9C6A6" }}>Отменить</button>
          <button onClick={save} disabled={saving} className="h-10 px-5 rounded-xl text-[12.5px] disabled:opacity-60"
                  style={{ background: "#4A3728", color: "#F7EFE0" }} data-testid="mo-quick-client-save">
            {saving ? "Сохраняем..." : "Сохранить"}
          </button>
        </div>
      </div>
    </div>
  );
}

function OrderItemRow({ item, productTypes, onChange, onRemove, onCreateProductType, index, guestsCount }) {
  const productTypeName = productTypes.find((productType) => productType.id === item.product_type_id)?.name || "";
  const showCakePlanning = isCakeLikeProductType(productTypeName);
  return (
    <div className="rounded-xl p-3 bg-white" style={{ boxShadow: "inset 0 0 0 1px #EFE4D0" }} data-testid={`mo-new-order-item-${index}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-[11px] tracking-[0.22em] uppercase" style={{ color: "#8B6F4B" }}>Позиция #{index + 1}</div>
        <button type="button" onClick={onRemove}
                className="w-8 h-8 rounded-full grid place-items-center hover:bg-red-50"
                style={{ color: "#8A3F1E" }} aria-label="Удалить позицию"
                data-testid={`mo-new-order-item-remove-${index}`}>
          <Trash2 size={13} strokeWidth={1.7} />
        </button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
        <div className="md:col-span-2">
          <label className="text-[10.5px] tracking-[0.2em] uppercase" style={{ color: "#8B6F4B" }}>Тип продукта</label>
          <div className="mt-1">
            <ProductTypeField
              value={item.product_type_id || ""}
              productTypes={productTypes}
              onChange={(id) => onChange({ ...item, product_type_id: id })}
              onCreate={onCreateProductType}
              testid={`mo-new-order-item-type-${index}`}
            />
          </div>
        </div>
        <Field label="Количество">
          <Input value={String(item.quantity ?? 1)} onChange={(v) => onChange({ ...item, quantity: parseInt(v || "0", 10) || 0 })}
                 testid={`mo-new-order-item-qty-${index}`} />
        </Field>
        <div className="md:col-span-2">
          <Field label="Состав / вкус">
            <Input value={item.flavor_composition || ""} onChange={(v) => onChange({ ...item, flavor_composition: v })}
                   placeholder="Шоколад, фундук, солёная карамель" testid={`mo-new-order-item-flavor-${index}`} />
          </Field>
        </div>
        <Field label="Размер (текстом)">
          <Input value={item.size_display || ""} onChange={(v) => onChange({ ...item, size_display: v })}
                 placeholder="20 см, 3 яруса" testid={`mo-new-order-item-size-${index}`} />
        </Field>
      </div>
      {showCakePlanning && (
        <div className="mt-3">
          <CakePlanningHelper
            value={item.cake_planning || null}
            onChange={(cake_planning) => onChange({ ...item, cake_planning })}
            guestsCount={guestsCount}
            testid={`mo-new-order-item-${index}-cake-planning`}
          />
        </div>
      )}
    </div>
  );
}

/* ---------------------------------- Page --------------------------------- */
export default function MOOrderNew() {
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const initialClientId = sp.get("clientId") || null;

  const { mode, loading: aLoad } = useMOAccess();
  const canWrite = isWritable(mode);

  const [loading, setLoading] = useState(true);
  const [clients, setClients] = useState([]);
  const [productTypes, setProductTypes] = useState([]);
  const [defaultCurrency, setDefaultCurrency] = useState("EUR");

  const [quickClientOpen, setQuickClientOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const todayISO = new Date().toISOString().slice(0, 10);
  const todayDate = useMemo(() => { const d = new Date(); d.setHours(0,0,0,0); return d; }, []);

  const [form, setForm] = useState({
    client_id: initialClientId,
    event_type: "",
    event_date: null,
    event_time: "",
    guests_count: "",
    wishes: "",
    dislikes: "",
    design_notes: "",
    budget: "",
    currency_code: "EUR",
    delivery_type: "",
    delivery_address: "",
    delivery_date: null,
    delivery_time_window: "",
    delivery_cost: "",
    delivery_notes: "",
    recipient_is_client: true,
    recipient_name: "",
    recipient_phone: "",
    recipient_notes: "",
    // Sprint MO-UX-Simplify: optional prepayment captured at order creation.
    prepay_amount: "",
    prepay_method: "cash",
  });
  const [items, setItems] = useState([{ product_type_id: "", quantity: 1 }]);

  useEffect(() => {
    if (aLoad || mode === "no_access" || mode === "demo") return;
    (async () => {
      setLoading(true);
      try {
        const [cls, pts, s] = await Promise.all([listClients(), listProductTypes(), getSettings()]);
        setClients(cls);
        setProductTypes(pts);
        const cur = s?.default_currency_code || "EUR";
        setDefaultCurrency(cur);
        setForm((f) => ({ ...f, currency_code: cur }));
      } catch (e) { toast.error(formatApiError(e)); }
      finally { setLoading(false); }
    })();
  }, [aLoad, mode]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const selectedClient = clients.find((c) => c.id === form.client_id) || null;

  const submit = async () => {
    if (!form.client_id) return toast.error("Выберите клиента");
    if (!form.event_date) return toast.error("Укажите дату события");
    if (!form.currency_code || form.currency_code.length !== 3) return toast.error("Укажите валюту заказа (ISO)");
    const validItems = items.filter((it) => it.product_type_id && (it.quantity || 0) > 0);
    if (validItems.length === 0) return toast.error("Добавьте хотя бы одну позицию");

    setSaving(true);
    try {
      const orderPayload = {
        client_id: form.client_id,
        event_type: form.event_type || null,
        event_date: form.event_date,
        event_time: form.event_time || null,
        guests_count: form.guests_count ? parseInt(form.guests_count, 10) : null,
        wishes: form.wishes || null,
        dislikes: form.dislikes || null,
        design_notes: form.design_notes || null,
        budget: form.budget || null,
        currency_code: form.currency_code,
      };
      if (form.delivery_type) {
        orderPayload.delivery = {
          type: form.delivery_type,
          address: form.delivery_address || null,
          date: form.delivery_date || null,
          time_window: form.delivery_time_window || null,
          cost: form.delivery_type === "pickup" ? null : (form.delivery_cost || null),
          notes: form.delivery_notes || null,
        };
      }
      if (form.recipient_is_client || form.recipient_name || form.recipient_phone || form.recipient_notes) {
        orderPayload.recipient = {
          is_client: form.recipient_is_client,
          name: form.recipient_is_client ? (selectedClient?.name || null) : (form.recipient_name || null),
          phone: form.recipient_is_client ? (selectedClient?.phone || null) : (form.recipient_phone || null),
          notes: form.recipient_notes || null,
        };
      }

      const order = await createOrder(orderPayload);
      // Create items sequentially (each POST returns its own record).
      for (const it of validItems) {
        await createOrderItem({
          order_id: order.id,
          product_type_id: it.product_type_id,
          quantity: it.quantity,
          flavor_composition: it.flavor_composition || null,
          size_display: it.size_display || null,
          notes: it.notes || null,
          cake_planning: it.cake_planning || null,
        });
      }
      toast.success(`Заказ № ${displayOrderNumber(order.order_number)} создан`);
      // Sprint MO-UX-Simplify: optional prepayment captured at create.
      const prepayNum = parseFloat(String(form.prepay_amount || "").replace(",", "."));
      if (Number.isFinite(prepayNum) && prepayNum > 0) {
        try {
          await createOrderPayment({
            order_id: order.id,
            amount_minor: Math.round(prepayNum * 100),
            currency_code: form.currency_code,
            method: form.prepay_method || "cash",
            type: "prepayment",
            paid_at: new Date().toISOString(),
          });
        } catch (e) {
          toast.error("Заказ создан, но не удалось внести предоплату: " + formatApiError(e));
        }
      }
      nav(`/master-order/orders/${order.id}`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const createProductTypeInline = async (name) => {
    const nm = String(name || "").trim();
    if (!nm) return null;
    try {
      const pt = await createProductType({ name: nm, is_active: true });
      setProductTypes((v) => v.some((item) => item.id === pt.id) ? v : [...v, pt]);
      toast.success(`Тип «${pt.name}» добавлен`);
      return pt;
    } catch (e) { toast.error(formatApiError(e)); return null; }
  };

  return (
    <MOShell active="orders" title="Новый заказ" subtitle="Создание черновика">
      {aLoad ? <LoadingBlock /> : mode === "no_access" || mode === "demo" ? <NoAccessScreen /> :
       !canWrite ? (
         <div className="rounded-xl bg-white p-8 text-center" style={{ boxShadow: "inset 0 0 0 1px #EFE4D0" }}>
           <div className="text-[16px]" style={{ fontFamily: "'Cormorant Garamond', serif", color: "#2A1D14" }}>
             Режим просмотра — создание заказов недоступно.
           </div>
         </div>
       ) : loading ? <LoadingBlock /> : (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <button onClick={() => nav("/master-order/orders")}
                    className="text-[12.5px] inline-flex items-center gap-1.5"
                    style={{ color: "#8B6F4B" }}
                    data-testid="mo-new-order-back">
              <ArrowLeft size={13} strokeWidth={1.8} /> К списку заказов
            </button>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-[1.5fr_1fr] gap-4">
            {/* Left: main data */}
            <div className="space-y-4">
              {/* Client */}
              <section className="rounded-xl bg-white p-5" style={{ boxShadow: "inset 0 0 0 1px #EFE4D0" }}>
                <div className="text-[17px] mb-3" style={{ fontFamily: "'Cormorant Garamond', serif", color: "#2A1D14" }}>
                  Клиент
                </div>
                <ClientPicker clients={clients} value={form.client_id}
                              onChange={(id) => {
                                const nextClient = clients.find((item) => item.id === id);
                                setForm((current) => ({
                                  ...current,
                                  client_id: id,
                                  ...(current.recipient_is_client ? {
                                    recipient_name: nextClient?.name || "",
                                    recipient_phone: nextClient?.phone || "",
                                  } : {}),
                                }));
                              }}
                              onCreate={() => setQuickClientOpen(true)} />
              </section>

              {/* Event */}
              <section className="rounded-xl bg-white p-5" style={{ boxShadow: "inset 0 0 0 1px #EFE4D0" }}>
                <div className="text-[17px] mb-3" style={{ fontFamily: "'Cormorant Garamond', serif", color: "#2A1D14" }}>
                  Событие
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <Field label="Дата события *">
                    <DateField value={form.event_date} onChange={(v) => set("event_date", v)}
                               minDate={todayDate} testid="mo-new-order-event-date" />
                  </Field>
                  <Field label="Время (по желанию)">
                    <input type="time" value={form.event_time} onChange={(e) => set("event_time", e.target.value)}
                           className="w-full h-10 px-3 rounded-lg text-[13px] outline-none"
                           style={{ background: "#FBF5EA", boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}
                           data-testid="mo-new-order-event-time" />
                  </Field>
                  <Field label="Тип события">
                    <Input value={form.event_type} onChange={(v) => set("event_type", v)}
                           placeholder="Свадьба, день рождения..." testid="mo-new-order-event-type" />
                  </Field>
                  <Field label="Гостей (по желанию)">
                    <Input value={form.guests_count} onChange={(v) => set("guests_count", v.replace(/\D/g, ""))}
                           testid="mo-new-order-guests" />
                  </Field>
                  <Field label="Валюта заказа *">
                    <CurrencyField value={form.currency_code} onChange={(c) => set("currency_code", c)}
                                   testid="mo-new-order-currency" />
                  </Field>
                  <Field label="Клиентский бюджет">
                    <Input value={form.budget} onChange={(v) => set("budget", v.replace(/[^\d.,]/g, ""))}
                           placeholder="0.00" testid="mo-new-order-budget" />
                  </Field>
                </div>
              </section>

              {/* Items */}
              <section className="rounded-xl bg-white p-5" style={{ boxShadow: "inset 0 0 0 1px #EFE4D0" }}>
                <div className="flex items-center justify-between mb-3">
                  <div className="text-[17px]" style={{ fontFamily: "'Cormorant Garamond', serif", color: "#2A1D14" }}>
                    Позиции заказа
                  </div>
                  <button type="button" onClick={() => setItems((it) => [...it, { product_type_id: "", quantity: 1 }])}
                          className="h-9 px-3 rounded-xl text-[12.5px] inline-flex items-center gap-1.5"
                          style={{ boxShadow: "inset 0 0 0 1px #D9C6A6", color: "#4A3728", background: "#FFFFFF" }}
                          data-testid="mo-new-order-add-item">
                    <Plus size={13} strokeWidth={1.8} /> Ещё позиция
                  </button>
                </div>
                <div className="space-y-3">
                  {items.map((it, idx) => (
                    <OrderItemRow key={idx} item={it} index={idx} productTypes={productTypes}
                                  onChange={(v) => setItems((cur) => cur.map((x, i) => i === idx ? v : x))}
                                  onCreateProductType={createProductTypeInline}
                                  guestsCount={form.guests_count}
                                  onRemove={() => setItems((cur) => cur.filter((_, i) => i !== idx))} />
                  ))}
                </div>
              </section>

              {/* Wishes */}
              <section className="rounded-xl bg-white p-5" style={{ boxShadow: "inset 0 0 0 1px #EFE4D0" }}>
                <div className="text-[17px] mb-3" style={{ fontFamily: "'Cormorant Garamond', serif", color: "#2A1D14" }}>
                  Пожелания
                </div>
                <div className="space-y-3">
                  <Field label="Пожелания / дизайн">
                    <textarea value={form.wishes} onChange={(e) => set("wishes", e.target.value)}
                              placeholder="Оформление, надписи, декор, референсы, живые цветы, топпер, съёмный декор — важные детали именно этого заказа."
                              className="w-full h-28 p-3 rounded-lg text-[13px] outline-none resize-y"
                              style={{ background: "#FBF5EA", boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}
                              data-testid="mo-new-order-wishes" />
                  </Field>
                </div>
              </section>
            </div>

            {/* Right: delivery + recipient + submit */}
            <div className="space-y-4">
              <section className="rounded-xl bg-white p-5" style={{ boxShadow: "inset 0 0 0 1px #EFE4D0" }}>
                <div className="text-[17px] mb-3" style={{ fontFamily: "'Cormorant Garamond', serif", color: "#2A1D14" }}>
                  Получение
                </div>
                <div className="space-y-3">
                  <Field label="Способ">
                    <StudioSelect value={form.delivery_type} onChange={(value) => set("delivery_type", value)}
                                  options={[{ value: "", label: "Не выбрано" }, ...DELIVERY_TYPES]}
                                  testid="mo-new-order-delivery-type" />
                  </Field>
                  {form.delivery_type && (
                    <>
                      <Field label={form.delivery_type === "pickup" ? "Адрес самовывоза" : "Адрес"}>
                        <Input value={form.delivery_address} onChange={(v) => set("delivery_address", v)}
                               placeholder="Город, улица, дом..." testid="mo-new-order-delivery-address" />
                      </Field>
                      <Field label={form.delivery_type === "pickup" ? "Дата получения" : "Дата доставки"}>
                        <DateField value={form.delivery_date} onChange={(v) => set("delivery_date", v)}
                                   testid="mo-new-order-delivery-date" />
                      </Field>
                      <Field label={form.delivery_type === "pickup" ? "Время получения" : "Временное окно"}>
                        <Input value={form.delivery_time_window} onChange={(v) => set("delivery_time_window", v)}
                               placeholder="11:00 - 13:00" />
                      </Field>
                      {form.delivery_type !== "pickup" && (
                        <Field label="Стоимость доставки">
                          <Input value={form.delivery_cost} onChange={(v) => set("delivery_cost", v.replace(/[^\d.,]/g, ""))}
                                 placeholder="0.00" />
                        </Field>
                      )}
                      <Field label={form.delivery_type === "pickup" ? "Комментарий к получению" : "Заметка"}>
                        <textarea value={form.delivery_notes} onChange={(e) => set("delivery_notes", e.target.value)}
                                  className="w-full h-16 p-3 rounded-lg text-[13px] outline-none resize-none"
                                  style={{ background: "#FBF5EA", boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }} />
                      </Field>
                    </>
                  )}
                </div>
              </section>

              <section className="rounded-xl bg-white p-5" style={{ boxShadow: "inset 0 0 0 1px #EFE4D0" }}>
                <div className="text-[17px] mb-3" style={{ fontFamily: "'Cormorant Garamond', serif", color: "#2A1D14" }}>
                  Получатель
                </div>
                <div className="space-y-3">
                  <button type="button" onClick={() => setForm((current) => ({
                    ...current,
                    recipient_is_client: !current.recipient_is_client,
                    ...(!current.recipient_is_client ? {
                      recipient_name: selectedClient?.name || "",
                      recipient_phone: selectedClient?.phone || "",
                    } : {}),
                  }))}
                    className="flex min-h-10 w-full items-center gap-2 rounded-xl bg-[#FBF5EA] px-3 text-left text-[13px] text-[#2A1D14] shadow-[inset_0_0_0_1px_#EFE4D0]"
                    aria-pressed={form.recipient_is_client} data-testid="mo-new-order-recipient-is-client">
                    <span className={`grid h-5 w-5 shrink-0 place-items-center rounded-md ${form.recipient_is_client ? "bg-[#53604A] text-white" : "bg-white text-transparent shadow-[inset_0_0_0_1px_#D9C6A6]"}`}><Check size={12} /></span>
                    Получатель — клиент
                  </button>
                  {form.recipient_is_client ? (
                    <div className="rounded-xl bg-[#FBF5EA] p-3 text-[12.5px] text-[#5B4326]">
                      <div>{selectedClient?.name || "Сначала выберите клиента"}</div>
                      {selectedClient?.phone && <div className="mt-0.5 text-[#8B6F4B]">{selectedClient.phone}</div>}
                    </div>
                  ) : (
                    <>
                      <Field label="Имя"><Input value={form.recipient_name} onChange={(v) => set("recipient_name", v)} testid="mo-new-order-recipient-name" /></Field>
                      <Field label="Телефон"><Input value={form.recipient_phone} onChange={(v) => set("recipient_phone", v)} placeholder="+..." /></Field>
                    </>
                  )}
                  <Field label="Заметка">
                    <textarea value={form.recipient_notes} onChange={(e) => set("recipient_notes", e.target.value)}
                              className="w-full h-16 p-3 rounded-lg text-[13px] outline-none resize-none"
                              style={{ background: "#FBF5EA", boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }} />
                  </Field>
                </div>
              </section>

              {/* Optional prepayment (Sprint MO-UX-Simplify) */}
              <section className="rounded-xl bg-white p-5" style={{ boxShadow: "inset 0 0 0 1px #EFE4D0" }}>
                <div className="text-[17px] mb-1" style={{ fontFamily: "'Cormorant Garamond', serif", color: "#2A1D14" }}>
                  Предоплата
                </div>
                <div className="text-[11.5px] mb-3" style={{ color: "#8B6F4B" }}>
                  Если клиент уже внёс деньги, укажите сумму — она появится в истории оплат.
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <Field label={`Сумма, ${form.currency_code || "EUR"}`}>
                    <input value={form.prepay_amount}
                           type="text" inputMode="decimal"
                           onChange={(e) => set("prepay_amount", e.target.value.replace(/[^\d.,]/g, ""))}
                           placeholder="например, 40"
                           className="w-full h-11 px-3 rounded-md bg-white text-base outline-none tabular-nums"
                           style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}
                           data-testid="mo-new-order-prepay-amount" />
                  </Field>
                  <Field label="Способ оплаты">
                    <StudioSelect value={form.prepay_method} onChange={(value) => set("prepay_method", value)}
                      options={[
                        { value: "cash", label: "Наличные" },
                        { value: "bank_transfer", label: "Банковский перевод" },
                        { value: "card", label: "Карта" },
                        { value: "other", label: "Другое" },
                      ]} testid="mo-new-order-prepay-method" />
                  </Field>
                </div>
              </section>

              <div className="rounded-xl bg-white p-4 flex flex-col md:flex-row md:items-center gap-2 justify-end sticky bottom-4"
                   style={{ boxShadow: "0 8px 22px -12px rgba(58,43,31,0.35), inset 0 0 0 1px #EFE4D0" }}>
                <button onClick={() => nav("/master-order/orders")}
                        className="h-10 px-4 rounded-xl text-[12.5px]"
                        style={{ background: "#FFFFFF", color: "#4A3728", boxShadow: "inset 0 0 0 1px #D9C6A6" }}>
                  Отменить
                </button>
                <button onClick={submit} disabled={saving}
                        className="h-11 px-5 rounded-xl text-[13px] inline-flex items-center gap-2 disabled:opacity-60"
                        style={{ background: "#4A3728", color: "#F7EFE0" }}
                        data-testid="mo-new-order-submit">
                  <Save size={14} strokeWidth={1.8} /> {saving ? "Создание..." : "Создать заказ"}
                </button>
              </div>
            </div>
          </div>

          <QuickClientModal open={quickClientOpen} onClose={() => setQuickClientOpen(false)}
                            onCreated={(c) => { setClients((v) => [c, ...v]); setForm((f) => ({
                              ...f, client_id: c.id,
                              ...(f.recipient_is_client ? { recipient_name: c.name || "", recipient_phone: c.phone || "" } : {}),
                            })); setQuickClientOpen(false); }} />
        </div>
      )}
    </MOShell>
  );
}
